/**
 * HandClassClassifier — classifies a hand (hole cards + board) into one of
 * eight strategic classes per /reference/02 and game engine spec §6.2.
 *
 *   cpfs        Current Pure Fucking Stone — strongest made hands (set+)
 *   thick_value Overpair / top pair good kicker — clear value
 *   thin_value  Top pair weak kicker / middle pair — bet 2 streets
 *   combo_draw  Pair + flush draw OR straight draw + flush draw
 *   he_draw     Flush draw alone OR OESD alone (≥8 outs)
 *   le_draw     Gutshot (4–7 outs)
 *   sdv         Showdown value (no pair: ace-high / two broadway; or under-pair)
 *   air         No pair, no draw, no showdown value
 *
 * OPS-3 closure note (per /design/specs/4_architecture_c4_l3_components.md §6.2 +
 * /design/specs/5_game_engine_spec.md §6.2): roughly 10–15% boundary disagreement
 * with `/reference`'s strategic interpretation is acknowledged. The engine
 * commits to consistent mechanical rules; the LLM grader may classify hero
 * differently at edges; neither is treated as a bug.
 *
 * Known mechanical-vs-strategic divergences (V1):
 *   - 55 on 954sss: mechanical = cpfs (set); /ref = thick_value (vulnerable
 *     to flush). Set is set; we don't downgrade based on board threats.
 *   - A9 on 954sss: mechanical = thick_value (top pair, ace kicker on top
 *     board card 9); /ref = thin_value (vulnerable to flush). Mechanical
 *     ignores board-threat context.
 *   - KQ-on-monotone-non-suit board: mechanical = sdv (two broadway);
 *     /ref = air (KQ off-suit on monotone hearts has no real equity).
 */

import { type Card, type Rank, RANK_VALUE } from '@/types/card';
import {
  STRAIGHTS_BY_HIGH,
  rankCounts,
  straightRanksFor,
  suitCounts,
  topRank,
} from './handEvaluator';
import { evaluateHandCategory, type HandCategory } from './handRank';

export type HandClass =
  | 'cpfs'
  | 'thick_value'
  | 'thin_value'
  | 'combo_draw'
  | 'he_draw'
  | 'le_draw'
  | 'sdv'
  | 'air';

/** Categories that always classify as cpfs (anything stronger than one pair). */
const CPFS_CATEGORIES: ReadonlySet<HandCategory> = new Set<HandCategory>([
  'Set',
  'TwoPair',
  'Straight',
  'Flush',
  'FullHouse',
  'Quads',
  'StraightFlush',
]);

/**
 * Classify a hand. The board can be 3, 4, or 5 cards (flop, turn, or river).
 */
export function classifyHand(
  holeCards: readonly [Card, Card],
  board: readonly Card[],
): HandClass {
  if (board.length === 0) {
    throw new Error('classifyHand: empty board');
  }

  const combined: Card[] = [...holeCards, ...board];
  const category = evaluateHandCategory(combined);

  // 1. cpfs — anything stronger than one pair
  if (CPFS_CATEGORIES.has(category)) return 'cpfs';

  // Compute draw signals before branching further, since OnePair + flush-draw
  // is combo_draw (not thick/thin) per /reference/02.
  const flushDrawing = hasFlushDraw(holeCards, board);
  const straightOuts = countStraightOuts(holeCards, board);

  // 2. OnePair branch
  if (category === 'OnePair') {
    // Pair + flush draw → combo_draw (overrides thick/thin value)
    if (flushDrawing) return 'combo_draw';
    return classifyPairedHand(holeCards, board);
  }

  // 3. No made hand — draws
  if (flushDrawing && straightOuts >= 4) return 'combo_draw';
  if (flushDrawing) return 'he_draw';
  if (straightOuts >= 8) return 'he_draw';
  if (straightOuts >= 4) return 'le_draw';

  // 4. SDV vs air
  return hasShowdownValue(holeCards) ? 'sdv' : 'air';
}

// =============================================================================
// Pair-branch helpers
// =============================================================================

/**
 * Within the OnePair branch: classify as thick_value, thin_value, or sdv.
 *
 * Rules:
 *   - Pocket pair > top board rank → thick_value (overpair)
 *   - Pocket pair ≤ top board rank → sdv (underpair per /reference/02)
 *   - Top pair (hole rank matches top board): kicker ≥ K → thick_value;
 *     else thin_value
 *   - Second / lower pair from hole → thin_value
 *   - Board-only pair (player has no contribution) → thin_value
 */
function classifyPairedHand(
  holeCards: readonly [Card, Card],
  board: readonly Card[],
): HandClass {
  const combined: Card[] = [...holeCards, ...board];
  const counts = rankCounts(combined);
  const pairedRank = findPairedRank(counts);
  if (pairedRank === null) {
    throw new Error('classifyPairedHand: no pair found in combined hand');
  }

  const holeContributionToPair = holeCards.filter(
    (c) => c.rank === pairedRank,
  ).length;
  const topBoardValue = RANK_VALUE[topRank(board)];
  const pairValue = RANK_VALUE[pairedRank];

  if (holeContributionToPair === 2) {
    // Pocket pair (both hole cards same rank, no board match → no set)
    if (pairValue > topBoardValue) return 'thick_value'; // overpair
    return 'sdv'; // underpair per /reference/02
  }

  if (holeContributionToPair === 1) {
    if (pairValue === topBoardValue) {
      // Top pair — check kicker
      const kicker = holeCards.find((c) => c.rank !== pairedRank);
      if (kicker && RANK_VALUE[kicker.rank] >= RANK_VALUE.K) {
        return 'thick_value';
      }
      return 'thin_value';
    }
    return 'thin_value'; // second pair or lower from hole
  }

  // holeContributionToPair === 0 → board pair, player has no contribution
  return 'thin_value';
}

function findPairedRank(counts: Map<Rank, number>): Rank | null {
  for (const [rank, n] of counts.entries()) {
    if (n >= 2) return rank;
  }
  return null;
}

// =============================================================================
// Draw helpers
// =============================================================================

/**
 * True if the player has a flush draw — 4 cards of one suit in combined hand,
 * with at least one hole card contributing to that suit.
 *
 * (5+ of a suit = made flush, handled by evaluateHandCategory → cpfs branch.)
 */
function hasFlushDraw(
  holeCards: readonly [Card, Card],
  board: readonly Card[],
): boolean {
  const combined = [...holeCards, ...board];
  const suits = suitCounts(combined);
  for (const [suit, n] of suits.entries()) {
    if (n === 4 && holeCards.some((c) => c.suit === suit)) return true;
  }
  return false;
}

/**
 * Count straight outs (cards remaining in the deck that complete a straight).
 *
 * Each straight window where combined cards have exactly 4 of the 5 ranks
 * contributes 4 outs (for the 1 missing rank). Multiple windows requiring the
 * same missing rank are deduplicated. The player must have at least one hole
 * rank in the window (otherwise the "draw" is a board-only draw the player
 * doesn't benefit from preferentially).
 */
function countStraightOuts(
  holeCards: readonly [Card, Card],
  board: readonly Card[],
): number {
  const combinedRanks = new Set([...holeCards, ...board].map((c) => RANK_VALUE[c.rank]));
  const holeRanks = new Set(holeCards.map((c) => RANK_VALUE[c.rank]));
  const outRanks = new Set<number>();

  for (const high of STRAIGHTS_BY_HIGH) {
    const window = straightRanksFor(high);
    const present = window.filter((r) => combinedRanks.has(r)).length;
    if (present !== 4) continue;
    if (!window.some((r) => holeRanks.has(r))) continue; // hole must participate
    const missing = window.find((r) => !combinedRanks.has(r));
    if (missing !== undefined) outRanks.add(missing);
  }

  return outRanks.size * 4; // 4 cards per missing rank in the deck
}

/**
 * Showdown-value heuristic per game engine spec §6.2: ace-high or two broadway
 * (T+) hole cards. Boundary cases (e.g. KQ on a monotone non-suit board where
 * the hand has no real equity) are knowingly classified as sdv even though
 * /reference may treat them as air — see module-level OPS-3 note.
 */
function hasShowdownValue(holeCards: readonly [Card, Card]): boolean {
  const r1 = RANK_VALUE[holeCards[0].rank];
  const r2 = RANK_VALUE[holeCards[1].rank];
  const hasAce = r1 === RANK_VALUE.A || r2 === RANK_VALUE.A;
  const broadwayCount =
    (r1 >= RANK_VALUE.T ? 1 : 0) + (r2 >= RANK_VALUE.T ? 1 : 0);
  return hasAce || broadwayCount >= 2;
}
